import { signInWithEmailAndPassword } from "firebase/auth";
import { Schema } from "nutso";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Form } from "../form/Form";
import { FormTextInput } from "../form/FormTextInput";
import { useValidation } from "../hooks/useValidation";
import { firebaseAuth } from "../services/firebaseAuth";
import { signInWithGooglePopup } from "../services/signInWithGooglePopup";
import { signInWithMicrosoftPopup } from "../services/signInWithMicrosoftPopup";
import { asyncOperation } from "../utils/asyncOperation";
import { useAuth } from "./AuthProvider";
import { usePortal } from "./PortalProvider";

type LoginInfo = {
  email: string;
  password: string;
};

const schema: Schema<LoginInfo> = {
  type: "object",
  properties: {
    email: {
      type: "string",
    },
    password: {
      type: "string", // TODO
    },
  },
};

type Props = {
  onChange: () => void;
};

export const LoginWidget = (props: Props) => {
  const [info, setInfo] = useState<LoginInfo>({
    email: "",
    password: "",
  });

  const [busy, setBusy] = useState(false);
  const validation = useValidation(info, schema);
  const { user } = useAuth();
  const { isAdminPortal } = usePortal();

  const onLogin = async () => {
    await asyncOperation({
      fn: () => signInWithEmailAndPassword(firebaseAuth, info.email, info.password),
      loadingMessage: `Logging in..`,
      setBusyFlag: (busy: boolean) => setBusy(busy),
      successMessage: "Successfully logged in",
    });
  };

  const handleGoogleSignIn = async () => {
    await asyncOperation({
      fn: () => signInWithGooglePopup(),
      loadingMessage: `Logging in..`,
      setBusyFlag: (busy: boolean) => setBusy(busy),
      successMessage: "Successfully logged in",
    });
  };

  const handleMicrosoftSignIn = async () => {
    await asyncOperation({
      fn: () => signInWithMicrosoftPopup(),
      loadingMessage: `Logging in..`,
      setBusyFlag: (busy: boolean) => setBusy(busy),
      successMessage: "Successfully logged in",
    });
  };

  if (user) {
    return <div>Already logged-in as {user.email}.</div>;
  }

  return (
    <div className="login-widget">
      <div className="form-header">
        <div className="heading">Login</div>
        <div className="sub-heading">
          Login with your personal email to manage your organisation’s work email addresses.
        </div>
      </div>
      <Form type="default" submit={{ label: "Login", handler: onLogin }} validationResult={validation} loading={busy}>
        <FormTextInput
          validation={validation.properties.email}
          label="Username"
          value={info.email}
          additionalClassNames="input-label"
          onChange={(value) => setInfo((prev) => ({ ...prev, email: value }))}
          type="email"
          dataCy="auth-email"
        />
        <FormTextInput
          validation={validation.properties.password}
          label="Password"
          value={info.password}
          additionalClassNames="input-label"
          onChange={(value) => setInfo((prev) => ({ ...prev, password: value }))}
          type="password"
          dataCy="auth-password"
        />
        <Link to="/forget-password" className="forget-password">
          Forgot password?
        </Link>
      </Form>
      {isAdminPortal && (
        <div className="form-actions">
          <div className="divider">
            <div className="rectangle"></div>
            <div className="or">or</div>
            <div className="rectangle"></div>
          </div>
          <button className="card-button" onClick={handleGoogleSignIn}>
            Login with Google
          </button>
          <button className="card-button" onClick={handleMicrosoftSignIn}>
            Login with Outlook
          </button>
          <div className="signup-message">
            Do not have an account?&nbsp;
            <span onClick={props.onChange}>
              <a href="#">Sign up</a>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
